import { map, pick, each, object, find, filter } from 'underscore';
import Promise from 'bluebird';
import {
  setMTImageModel,
  getFlattenedImageAssetList,
  updateAssetsIdsAsync,
} from '@bingads-webui-campaign-react/responsive-ad-editor-utils';
import { thirdPartyMeasurementStringToViewModel, getVerifiedTrackingSettingWrite } from '@bingads-webui-campaign-react/third-party-measurement';

function formatIntoTextArrayString(texts) {
  let res = '';
  map(texts, (textValue = '') => {
    // To escape double quotes in the text value to avoid bulkupsert failure
    res += `${res === '' ? '' : ','}{"Text":"${textValue.replace(/"/g, '\\"')}"}`;
  });
  return `[${res}]`;
}

const formatHotspots = (hotspots) => {
  const hotspotsArray = map(hotspots, (hotspot) => {
    const hotspotObj = {
      Title: hotspot.title,
      ActionText: hotspot.actionText,
      Description: hotspot.desc,
      Uri: hotspot.uri,
      Glyph: hotspot.glyph,
      Placement: hotspot.placement,
    };
    return hotspotObj;
  });
  return hotspotsArray;
};

const getProcessedDataAsync = (originalData, imageList, imageOdata, permissions, UiUnsupportedRatios = [], isImageFittingEnabled = false) => {
  // for portrait images we need to check if we are in display plus amlify and isamplifyenabled.
  // if it is boost campaigns mma v2 should be disabled.
  const isBoostCampaign = permissions.dynamic.DisplayPlusAmplify && permissions.IsAmplifyEnabled;

  const flattenedIncludedImageAssetList = getFlattenedImageAssetList(imageList, !isBoostCampaign, !isBoostCampaign);
  const imageIndexAsThumbnail = 0;

  return updateAssetsIdsAsync(flattenedIncludedImageAssetList, imageIndexAsThumbnail, !isBoostCampaign, imageOdata)
    .then((updates) => {
      const originalThumbnailAsset = flattenedIncludedImageAssetList[imageIndexAsThumbnail];
      const overrideInfo = updates[imageIndexAsThumbnail];
      let imageToGenerateThumbnail;

      if (overrideInfo) {
        imageToGenerateThumbnail = originalThumbnailAsset.copyThumbnailLookToNewImage({
          id: overrideInfo.id,
          src: overrideInfo.bingSrc,
        });
      } else {
        imageToGenerateThumbnail = originalThumbnailAsset;
      }

      const portraitImage = find(imageList, items => items.find(item => item.isPortrait));

      setMTImageModel(
        originalData,
        updates,
        map(imageList, imageArray => filter(imageArray, imageObj => imageObj.isIncluded)) /* imageList */,
        {
          isMultimediaAdinMSANEnabled: !isBoostCampaign,
          isMultimediaAd: false,
          isMMAV2Enabled: !isBoostCampaign,
          isBoostPortraitImageEnabled: isBoostCampaign, // this.isBoostPortraitImageEnabled,
          isBoost200x100ImageEnabled: permissions && permissions.IsBoost200x100ImageEnabled, // this.isBoost200x100ImageEnabled,
          isBoostNewAspectRatioSupportEnabled: permissions && permissions.IsBoostNewAspectRatioSupportEnabled, //  this.isBoostNewAspectRatioSupportEnabled,
          isImageFittingEnabled,
        },
        UiUnsupportedRatios,
        portraitImage && portraitImage[0]
      );

      return {
        mtModel: originalData,
        imageToGenerateThumbnail,
      };
    });
};

export const viewModelToModelForAd = ({
  adsViewModel, campaign, adGroup, imageOdata, permissions, isImageFittingEnabled = false,
}, isDeletedEntities = false) => {
  // parse the ads
  const adViewModel = [];

  let images;
  let videos;

  if (adsViewModel.length === 0) {
    return Promise.resolve(adViewModel);
  }

  const ad = adsViewModel[0];

  if (ad.videos && Array.isArray(ad.videos)) {
    videos = ad.videos.map(video => ({
      id: video.id,
      idString: video.idString,
      subType: video.type,
    }));
  }

  const fields = {
    'Client Id': '0', // `${ad.uid}`,
    FinalUrls: ad.FinalUrls,
    Videos: JSON.stringify(videos),
    Images: JSON.stringify(images),
    MobileFinalUrls: ad.MobileFinalUrls,
    BusinessName: ad.BusinessName,
    Headlines: ad.Headlines ? formatIntoTextArrayString(ad.Headlines) : undefined,
    LongHeadlines: ad.LongHeadlines ? formatIntoTextArrayString(ad.LongHeadlines) : undefined,
    Descriptions: ad.Descriptions ? formatIntoTextArrayString(ad.Descriptions) : undefined,
    TrackingUrlTemplate: ad.TrackingUrlTemplate,
    FinalUrlSuffix: ad.FinalUrlSuffix,
    UrlCustomParameters: ad.UrlCustomParameters,
    ImpressionTrackingUrls: ad.ImpressionTrackingUrls,
    HotSpots: JSON.stringify(formatHotspots(ad.hotspots)),
    Campaign: pick(campaign, 'Id'),
    AdGroup: pick(adGroup, 'Id'),
    CallToAction: ad.CallToAction && ad.CallToAction.cta,
    CallToActionLanguage: ad.CallToAction && ad.CallToAction.language,
    CallToActionText: ad.CallToActionText,
    CallToActionAriaText: ad.CallToActionAriaText,
    BoostAnchors: ad.BoostAnchors,
    ...getVerifiedTrackingSettingWrite(ad.VerifiedTrackingSettings),
  };

  // only add call to action if we have the value set.
  if (ad.CallToActionAriaText) {
    fields.CallToActionAriaText = ad.CallToActionAriaText;
  }

  if (ad.Id) {
    fields.Id = ad.Id;
  }

  if (isDeletedEntities) {
    fields.Status = 'Deleted';
  }

  if (ad.Images && ad.Images.ImageList && imageOdata) {
    return getProcessedDataAsync(fields, ad.Images.ImageList, imageOdata, permissions, [], isImageFittingEnabled)
      .then(({ mtModel }) => {
        adViewModel.push(mtModel);

        return adViewModel;
      });
  }

  adViewModel.push(fields);
  return Promise.resolve(adViewModel);
};


const processTextAttribute = (jsonTextAttribute, name) =>
  object(JSON.parse(jsonTextAttribute).map((textAttribute, index) => ([`${name}${index}`, textAttribute.Text])));

export const bulkModelToViewModelForAd = ({
  adsModel,
  originalAdsModel,
}) => {
  const savedAd = adsModel[0];

  savedAd.Images = originalAdsModel.data.Images;
  savedAd.videos = originalAdsModel.data.videos || [];
  savedAd.Headlines = savedAd.Headlines ? processTextAttribute(savedAd.Headlines, 'Headline') : undefined;
  savedAd.LongHeadlines = savedAd.LongHeadlines ? processTextAttribute(savedAd.LongHeadlines, 'LongHeadline') : undefined;
  savedAd.Descriptions = savedAd.Descriptions ? processTextAttribute(savedAd.Descriptions, 'Description') : undefined;
  savedAd.VerifiedTrackingSettings = savedAd.VerifiedTrackingData ? thirdPartyMeasurementStringToViewModel(savedAd.VerifiedTrackingData) : undefined;
  savedAd.hotspots = originalAdsModel.data.hotspots || [];
  savedAd.CallToAction = {
    cta: savedAd.CallToAction,
    language: savedAd.CallToActionLanguage,
  };

  const adViewModel = {
    data: savedAd,
    isValid: true,
    isDirty: false,
    currentPageIndex: 1,
  };

  return adViewModel;
};


export const modelToViewModelForAd = ({
  adsModel,
}) => {
  const adsVideoModel = { ...adsModel };

  return adsVideoModel;
};

export const splitUpdateAndCreateAds = ({
  adsViewModel, oldAdsModel, campaign, adGroup, imageOdata, permissions, isImageFittingEnabled = false,
}) => {
  const adIds = [];

  const adsToCreate = [];
  const adsToUpdate = [];
  const adsToDelete = [];

  each([adsViewModel], (ad) => {
    if (ad.Images && ad.Images.ImageList) {
      // check if the ad has ID
      if (ad.Id) {
        adsToUpdate.push(ad);

        // add the Ids of Ads that have already been saved to API
        // so that we can delete if the oldAdsModel contains more Ids
        adIds.push(ad.Id);
      } else {
        adsToCreate.push(ad);
      }
    }
  });

  if (oldAdsModel) {
    // get ids of the ads that are deleted (missing in adIds array)
    each([oldAdsModel], (ad) => {
      if (ad.Images && ad.Images.ImageList) {
        adsToDelete.push(ad);
      }
    });
  }

  const promises = {
    createAds: viewModelToModelForAd({
      adsViewModel: adsToCreate, campaign, adGroup, imageOdata, permissions, isImageFittingEnabled,
    }),
    updateAds: viewModelToModelForAd({
      adsViewModel: adsToUpdate, campaign, adGroup, imageOdata, permissions, isImageFittingEnabled,
    }),
    deleteAds: viewModelToModelForAd({
      adsViewModel: adsToDelete, campaign, adGroup, imageOdata, permissions, isImageFittingEnabled,
    }, true),
  };

  return Promise.props(promises).then((results) => {
    const updatedAds = results.updateAds || [];
    const createdAds = results.createAds || [];
    const deletedAds = results.deleteAds || [];

    const adsNeededToCreate = createdAds;

    const adsNeededToUpdate = [...updatedAds, ...deletedAds];

    return { adsNeededToCreate, adsNeededToUpdate };
  });
};
